import React, { memo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.js';

// Helper function to validate HTTP URLs
function isSafeHttpUrl(src: string): boolean {
  try {
    const url = new URL(src);
    const hostname = url.hostname.toLowerCase();

    // Check protocol
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      return false;
    }

    // Block localhost and common local names
    if (hostname === 'localhost' || hostname === '::1') {
      return false;
    }

    // Check for IPv4 addresses
    const ipv4Regex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
    const ipv4Match = hostname.match(ipv4Regex);
    if (ipv4Match) {
      const [, a, b, c, d] = ipv4Match.map(Number);

      // Validate IP range (0-255)
      if (a > 255 || b > 255 || c > 255 || d > 255) {
        return false;
      }

      // Block loopback (127.0.0.0/8)
      if (a === 127) {
        return false;
      }

      // Block private networks (RFC 1918)
      if (a === 10) {
        return false;
      }
      if (a === 172 && b >= 16 && b <= 31) {
        return false;
      }
      if (a === 192 && b === 168) {
        return false;
      }

      // Block link-local (169.254.0.0/16)
      if (a === 169 && b === 254) {
        return false;
      }

      // Block 0.0.0.0
      if (a === 0 && b === 0 && c === 0 && d === 0) {
        return false;
      }
    }

    // Check for IPv6 addresses
    if (hostname.includes(':')) {
      if (hostname === '::1' || hostname === '0:0:0:0:0:0:0:1') {
        return false;
      }
      if (hostname.startsWith('fc') || hostname.startsWith('fd')) {
        return false;
      }
      if (
        hostname.startsWith('fe8') ||
        hostname.startsWith('fe9') ||
        hostname.startsWith('fea') ||
        hostname.startsWith('feb')
      ) {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
}

// Auto-linkify plain text URLs
function linkifyText(text: string): React.ReactNode {
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      if (!isSafeHttpUrl(part)) {
        return (
          <span
            key={index}
            className="text-muted-foreground underline decoration-dotted"
            title="Unsafe URL"
          >
            {part}
          </span>
        );
      }
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline-offset-2 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium break-all"
          title={part}
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

// Code block component with copy functionality
const CodeBlock = ({
  className,
  children,
  ...props
}: {
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}) => {
  const [copied, setCopied] = useState(false);
  const text = String(children ?? '').replace(/\n$/, '');
  const isInline = !className;

  if (isInline) {
    return (
      <code
        className="text-xs px-1.5 py-0.5 bg-muted rounded font-mono break-all"
        {...props}
      >
        {children}
      </code>
    );
  }

  return (
    <div className="relative group my-4 min-w-0 max-w-full">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              navigator.clipboard
                .writeText(text)
                .then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                })
                .catch(() => {});
            }}
            className="absolute right-2 top-2 z-10 h-7 w-7 opacity-70 hover:opacity-100 transition-opacity"
          >
            {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{copied ? 'Copied!' : 'Copy code'}</p>
        </TooltipContent>
      </Tooltip>
      <pre className="overflow-auto bg-muted p-3 rounded-lg text-sm max-w-full">
        <code className={className}>{text}</code>
      </pre>
    </div>
  );
};

// Enhanced markdown component with image support
const MarkdownTextImpl = ({ children }: { children: string }) => {
  return (
    <div className="prose prose-sm max-w-none dark:prose-invert min-w-0 overflow-hidden break-words [&>p]:my-4 [&>p]:leading-7 [&>p]:first:mt-0 [&>p]:last:mb-0 [&>h1]:mb-6 [&>h1]:text-3xl [&>h1]:font-bold [&>h1]:last:mb-0 [&>h2]:mb-4 [&>h2]:mt-6 [&>h2]:text-2xl [&>h2]:font-semibold [&>h2]:first:mt-0 [&>h2]:last:mb-0 [&>h3]:mb-3 [&>h3]:mt-5 [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:first:mt-0 [&>h3]:last:mb-0 [&>ul]:my-4 [&>ul]:ml-6 [&>ul]:list-disc [&>ul>li]:mt-1 [&>ol]:my-4 [&>ol]:ml-6 [&>ol]:list-decimal [&>ol>li]:mt-1 [&>blockquote]:border-l-2 [&>blockquote]:pl-4 [&>blockquote]:italic [&>hr]:my-4">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        skipHtml={true}
        components={{
          a: ({ href, children, ...props }) => {
            const url = (href as string | undefined) ?? '';
            const isHttp = /^https?:\/\//i.test(url);
            if (!isHttp || !isSafeHttpUrl(url)) {
              return (
                <span className="text-muted-foreground underline decoration-dotted" {...props}>
                  {children}
                </span>
              );
            }
            return (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline-offset-2 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium break-all"
                title={url}
                {...props}
              >
                {children}
              </a>
            );
          },
          img: ({ src, alt, ...props }) => {
            if (!src) {
              return (
                <span className="text-xs text-muted-foreground">No image source provided</span>
              );
            }

            const srcString = typeof src === 'string' ? src : '';

            if (!srcString || !isSafeHttpUrl(srcString)) {
              return (
                <span className="text-xs text-muted-foreground">Invalid or unsafe image source</span>
              );
            }

            return (
              <img
                src={srcString}
                alt={alt || 'Image'}
                className="max-w-full max-h-[400px] object-contain rounded-lg border border-border my-4"
                loading="lazy"
                {...props}
              />
            );
          },
          p: ({ children, ...props }) => {
            // Auto-linkify plain text URLs in paragraphs
            if (typeof children === 'string') {
              return <p {...props}>{linkifyText(children)}</p>;
            }
            return <p {...props}>{children}</p>;
          },
          table: ({ className, children, ...props }) => (
            <div className="my-4 overflow-x-auto">
              <table
                className={['w-full border-separate border-spacing-0', className]
                  .filter(Boolean)
                  .join(' ')}
                {...props}
              >
                {children}
              </table>
            </div>
          ),
          thead: ({ className, ...props }) => <thead className={className} {...props} />,
          tr: ({ className, ...props }) => (
            <tr
              className={['m-0 border-b first:border-t', className].filter(Boolean).join(' ')}
              {...props}
            />
          ),
          th: ({ className, ...props }) => (
            <th
              className={[
                'bg-muted text-left font-bold align-top px-4 py-2 first:rounded-tl-lg last:rounded-tr-lg',
                className,
              ]
                .filter(Boolean)
                .join(' ')}
              {...props}
            />
          ),
          td: ({ className, ...props }) => (
            <td
              className={[
                'border-b border-l last:border-r text-left align-top px-4 py-2 whitespace-normal break-words',
                className,
              ]
                .filter(Boolean)
                .join(' ')}
              {...props}
            />
          ),
          code: CodeBlock,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
};

export const MarkdownText = memo(MarkdownTextImpl);
